/**
 * Les deux parcours d'ajout d'un site, et l'enchaînement de leurs étapes.
 *
 * Adopter une installation existante et en créer une neuve ne demandent pas la même chose : ni les
 * mêmes informations, ni les mêmes prérequis, et elles n'échouent pas pour les mêmes raisons. La
 * divergence reste donc entière — c'est **le moment du choix** qui a changé.
 *
 * Il se faisait en première étape, avant que quiconque ait regardé le serveur : on demandait « y
 * a-t-il déjà Ghost ici ? » à la seule personne qui n'était pas en mesure de le vérifier. Il se fait
 * maintenant après la connexion et la détection, où Vigie peut dire ce qu'elle a trouvé et proposer
 * la réponse la plus probable.
 *
 * **Proposer, et non décider.** Un hôte qui héberge déjà un site peut parfaitement en accueillir un
 * second — le test de nuit l'exige explicitement — donc « Ghost est présent ici » ne dit pas lequel
 * des deux gestes on est venu faire. Déduire l'intention de la détection se tromperait à chaque fois
 * qu'on ajoute un second média sur une machine, c'est-à-dire dans le cas même que le produit vise.
 *
 * Ce que les deux parcours gardent en commun : l'identité, la connexion, la vérification du
 * serveur, et surtout le **même enregistrement**. Un site installé par Vigie est inscrit par la même
 * route, avec la même vérification finale, qu'un site adopté. Cette convergence est l'invariant à
 * protéger : un second chemin d'enregistrement voudrait dire deux façons de créer un site, donc deux
 * façons de se tromper.
 */

/** Ce qu'on vient faire. Demandé une fois le serveur sondé, pas avant. */
export const INTENTIONS = ['existant', 'neuf'] as const;
export type Intention = (typeof INTENTIONS)[number];

export type Étape =
  | 'intention'
  | 'identité'
  | 'connexion'
  | 'serveur'
  | 'détection'
  | 'installation'
  | 'enregistrement'
  | 'fini';

/**
 * Étapes communes aux deux parcours, avant la divergence.
 *
 * L'intention les termine : c'est la dernière chose commune, et la première qui sépare.
 */
const AVANT: readonly Étape[] = ['identité', 'connexion', 'serveur', 'intention'];

/** Étapes communes après la divergence. */
const APRÈS: readonly Étape[] = ['enregistrement', 'fini'];

/**
 * Après l'installation d'un site neuf, il n'y a plus rien à saisir.
 *
 * L'installateur enregistre le site lui-même — il connaît les paramètres qu'il vient d'écrire, et
 * les faire ressaisir n'apporterait qu'une occasion de se tromper. L'étape d'enregistrement ne
 * concerne donc que l'adoption d'une installation existante.
 */
const APRÈS_NEUF: readonly Étape[] = ['fini'];

/**
 * Enchaînement du parcours retenu.
 *
 * Tant que l'intention n'est pas choisie, on ne montre pas d'étapes spéculatives : afficher les
 * deux suites laisserait croire qu'il faudra les traverser toutes les deux.
 */
export function étapesPour(intention: Intention | null): readonly Étape[] {
  if (intention === null) return AVANT;
  if (intention === 'neuf') return [...AVANT, 'installation', ...APRÈS_NEUF];
  return [...AVANT, 'détection', ...APRÈS];
}

export const LIBELLÉS_ÉTAPE: Record<Étape, string> = {
  intention: 'Que faire ici',
  identité: 'Identité du site',
  connexion: 'Moyen de connexion',
  serveur: 'Vérification du serveur',
  détection: 'Détection de Ghost',
  installation: 'Installation de Ghost',
  enregistrement: 'Enregistrement',
  fini: 'Terminé',
};

/** Étape suivante, ou `null` à la fin du parcours. */
export function étapeSuivante(intention: Intention | null, courante: Étape): Étape | null {
  const étapes = étapesPour(intention);
  const index = étapes.indexOf(courante);
  if (index === -1) return null;
  return étapes[index + 1] ?? null;
}

/**
 * Position dans le parcours, pour le fil d'étapes.
 *
 * Rend le rang **et** le total, parce qu'un « 4 » sans total ne dit pas s'il reste une étape ou
 * quatre.
 */
export function position(
  intention: Intention | null,
  courante: Étape,
): { rang: number; total: number } {
  const étapes = étapesPour(intention);
  return { rang: étapes.indexOf(courante), total: étapes.length };
}

/** Un indice de détection, réduit à ce dont l'orientation a besoin. */
export interface CandidatDétecté {
  installType: string;
  confidence: number;
}

/**
 * Au-dessus de ce seuil, un candidat repose sur un indice réel.
 *
 * La détection rend **toujours** un candidat : faute d'avoir rien trouvé, elle propose `custom` à
 * 20 %, avec pour seul indice « ni .ghost-cli, ni conteneur Ghost, ni fichier compose reconnu ».
 * Les vrais indices commencent à 45 %. Le seuil se place donc entre les deux, et distingue « je
 * n'ai rien vu » de « j'ai vu quelque chose » — la seule question qui intéresse l'orientation.
 */
const CONFIANCE_INDICE_RÉEL = 40;

/**
 * Ce que la détection suggère, une fois le serveur sondé.
 *
 * Rend une **suggestion**, que l'écran pré-sélectionne et qu'un clic remplace. Trouver Ghost ne
 * prouve pas qu'on vient l'adopter : sur un hôte qui héberge déjà un média, en installer un second
 * est un usage prévu et couvert par le test de nuit. Ce qui serait faux, c'est de décider à la place
 * de la personne ; ce qui est utile, c'est de lui épargner une question quand la réponse est
 * évidente.
 */
export function orientationAprèsDétection(candidats: readonly CandidatDétecté[]): {
  suggestion: Intention;
  ghostTrouvé: boolean;
} {
  const ghostTrouvé = candidats.some((candidat) => candidat.confidence >= CONFIANCE_INDICE_RÉEL);
  return { suggestion: ghostTrouvé ? 'existant' : 'neuf', ghostTrouvé };
}

/**
 * Les prérequis d'une installation, et ce qui manque quand elle est impossible.
 *
 * Docker n'en fait plus partie : Vigie l'installe. Ce qui reste bloquant est ce qu'elle ne peut pas
 * changer — un système qu'elle ne sait pas installer, et un compte qui ne peut pas élever ses
 * privilèges. Le dire avant de proposer le bouton évite de faire découvrir l'impossibilité par un
 * échec au bout de vingt minutes.
 */
export interface Aptitude {
  privileges: 'root' | 'sudo' | 'sudo_mdp_requis' | 'aucun';
  systeme: { nom: string; prisEnCharge: boolean };
}

export function prérequisInstallation(aptitude: Aptitude | null): {
  ok: boolean;
  raisons: string[];
} {
  if (aptitude === null) return { ok: false, raisons: ['La connexion n’a pas encore été testée.'] };
  const raisons: string[] = [];
  if (!aptitude.systeme.prisEnCharge) {
    raisons.push(
      `${aptitude.systeme.nom} n’est pas pris en charge : Vigie installe sur Debian 12 ou 13 et Ubuntu 22.04, 24.04 ou 26.04.`,
    );
  }
  if (aptitude.privileges === 'aucun') {
    raisons.push(
      'Ce compte ne peut pas élever ses privilèges, et poser Docker en a besoin : connecte-toi en root, ou avec un compte disposant de sudo.',
    );
  }
  return { ok: raisons.length === 0, raisons };
}
